import {
  CARD_PROVIDERS,
  CREDENTIAL_FIELDS,
  type CredentialField,
  credentialFieldProblem,
  currencySchema,
  PAYMENT_CREDENTIAL_FIELD_FOREIGN,
} from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { accountKindSchema } from './organization.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

export const paymentProviderSchema = z.enum(CARD_PROVIDERS);

export const paymentCredentialBlockerSchema = z.enum(['webhookBaseUrl', 'providerGateway']);
export type PaymentCredentialBlocker = z.infer<typeof paymentCredentialBlockerSchema>;
export type PaymentProvider = z.infer<typeof paymentProviderSchema>;

/**
 * **Everything a route ever says about a credential, and no field of it is
 * secret.** There is no reveal route and no schema here that could carry sealed
 * material — a key is replaced, never displayed
 * (docs/decisions/0053 §1, `.claude/rules/secrets.md`).
 *
 * `fingerprint` is the last four characters of the provider's own key, which is
 * how a person recognises which key is in place and is never enough to use one.
 * `clientRef` and `webhookRef` are PayPal's client id and webhook id, which
 * PayPal itself treats as public.
 */
export const paymentCredentialStateSchema = z.object({
  organizationAccountId: z.uuid(),
  accountLabel: z.string(),
  accountKind: accountKindSchema,
  accountCurrency: currencySchema,
  accountIsActive: z.boolean(),
  provider: paymentProviderSchema,
  configured: z.boolean(),
  isActive: z.boolean(),
  fingerprint: z.string().nullable(),
  keyId: z.string().nullable(),
  onCurrentKey: z.boolean(),
  clientRef: z.string().nullable(),
  webhookRef: z.string().nullable(),
  webhookUrl: z.string().nullable(),
  configuredAt: z.string().nullable(),
  configuredByName: z.string().nullable(),
  /**
   * What on the server stops this account taking money however right its keys
   * are — no `PAYMENT_WEBHOOK_BASE_URL` to build the webhook URL on, or no
   * gateway wired for the provider (PayPal without `PAYPAL_API_BASE_URL`,
   * NestPay always). Said for every account, configured or not, so the screen
   * can say it before anybody types a key (docs/decisions/0098).
   */
  blockedBy: z.array(paymentCredentialBlockerSchema),
  /** Active keys on an active account, and nothing in `blockedBy`: payments are processed. */
  ready: z.boolean(),
});

export type PaymentCredentialState = z.infer<typeof paymentCredentialStateSchema>;

const credentialValue = z.string().trim().max(4096).optional();

/**
 * Flat on purpose: a secret nested one level deeper than `body.<field>` would
 * fall outside `REDACTED_LOG_PATHS`' single wildcard and be logged.
 * `nestpay` is deferred, so the union of accepted providers is two — a third
 * arrives with its own fields (docs/decisions/0053 §6).
 *
 * **The shape of every key is judged here, and the server is the authority**:
 * `credentialFieldProblem` from `@btsis/core` is the same rule the screen runs,
 * and each refusal is an issue whose message is its reason code on the field's
 * own path, which `toORPCError` turns into a 422 the screen puts on that field
 * (docs/decisions/0098). Leading and trailing whitespace is trimmed, as a paste
 * carries it; whitespace inside a key is refused.
 */
const setInputSchema = z
  .object({
    organizationId: z.uuid(),
    accountId: z.uuid(),
    provider: z.enum(['stripe', 'paypal']),
    secretKey: credentialValue,
    webhookSecret: credentialValue,
    clientId: credentialValue,
    clientSecret: credentialValue,
    webhookId: credentialValue,
  })
  .superRefine((input, context) => {
    for (const field of CREDENTIAL_FIELDS[input.provider]) {
      const problem = credentialFieldProblem(field, input[field]);
      if (problem !== null) context.addIssue({ code: 'custom', path: [field], message: problem });
    }
    const foreign = (Object.keys(CREDENTIAL_FIELDS) as (keyof typeof CREDENTIAL_FIELDS)[])
      .filter((provider) => provider !== input.provider)
      .flatMap((provider): readonly CredentialField[] => CREDENTIAL_FIELDS[provider])
      .filter((field) => input[field] !== undefined && input[field] !== '');
    for (const field of foreign) {
      context.addIssue({
        code: 'custom',
        path: [field],
        message: PAYMENT_CREDENTIAL_FIELD_FOREIGN,
      });
    }
  });

/**
 * A dernek's own payment credentials. **Reads** are organization scope behind
 * `organization.view`, as every other organization read is; **writes** are
 * `organization.edit` at global scope alone, the gate the account row itself
 * has, because a credential is an attribute of an account and no new permission
 * is minted for it — docs/decisions/0053 §1, `.claude/rules/rbac-scope.md`.
 */
export const paymentCredentialContract = {
  list: oc
    .route({
      method: 'GET',
      path: '/organizations/{organizationId}/payment-credentials',
      summary: 'Whether each of an organization’s provider accounts is configured — never the keys',
    })
    .errors(guarded)
    .input(z.object({ organizationId: z.uuid() }))
    .output(
      z.object({
        credentials: z.array(paymentCredentialStateSchema),
        writable: z.boolean(),
        /** False where no key ring is in the environment: a write would have nothing to seal under. */
        sealingAvailable: z.boolean(),
        /** False where the server has no `PAYMENT_WEBHOOK_BASE_URL`: no webhook URL exists to register. */
        webhookBaseUrlConfigured: z.boolean(),
        /** True under `NODE_ENV=production`, where a Stripe test-mode key is refused. */
        liveKeysRequired: z.boolean(),
      }),
    ),

  set: oc
    .route({
      method: 'PUT',
      path: '/organizations/{organizationId}/accounts/{accountId}/payment-credential',
      summary: 'Set or replace the keys of one account. A key is replaced, never displayed',
    })
    .errors(guarded)
    .input(setInputSchema)
    .output(z.object({ credential: paymentCredentialStateSchema })),

  deactivate: oc
    .route({
      method: 'POST',
      path: '/organizations/{organizationId}/accounts/{accountId}/payment-credential/deactivate',
      summary: 'Stop drawing links on this account, keeping the sealed row for the trail',
    })
    .errors(guarded)
    .input(z.object({ organizationId: z.uuid(), accountId: z.uuid() }))
    .output(z.object({ credential: paymentCredentialStateSchema })),
};
